import type {
  ProjectGalleryCard,
  ProjectGalleryJunctionItem,
  ProjectGalleryPost,
  ProjectGalleryResult,
} from '~/types';
import { buildCardFromPost, buildProjectCards } from '~/utils/project-gallery';

/**
 * Resolve a `block_gallery`'s selected files into project cards.
 *
 * Two batched queries (no N+1):
 *   1. Reverse-map each block file → its project post via `post_gallery_items`.
 *   2. Fetch the full project posts (with ordered `gallery` relation).
 *
 * When `showAllProjects` is enabled, skips the reverse-map and fetches all
 * published project posts instead (used to render every project regardless of
 * the block's selected files).
 *
 * Returns `mode: 'file'` with empty cards when nothing resolves, so the caller
 * can fall back to the existing file gallery.
 */
export async function useProjectGallery(
  blockFileIds: string[],
  options: { showAllProjects?: boolean } = {},
): Promise<ProjectGalleryResult> {
  const { showAllProjects = false } = options;

  try {
    let junctionRows: ProjectGalleryJunctionItem[] = [];
    let postIds: string[] = [];

    if (!showAllProjects) {
      if (!blockFileIds || blockFileIds.length === 0) {
        return { cards: [], mode: 'file' };
      }

      // Query 1: reverse-map files → project posts
      junctionRows = await useDirectusProxy<ProjectGalleryJunctionItem[]>({
        operation: 'readItems',
        collection: 'post_gallery_items',
        params: {
          filter: { directus_files_id: { _in: blockFileIds } },
          fields: ['posts_id', 'directus_files_id', 'sort'],
          limit: -1,
        },
      });

      postIds = Array.from(
        new Set(
          (junctionRows ?? [])
            .map((row) => row.posts_id)
            .filter((id): id is string => !!id),
        ),
      );

      if (postIds.length === 0) {
        return { cards: [], mode: 'file' };
      }
    }

    // Query 2: fetch full project posts with ordered galleries
    const posts = await useDirectusProxy<ProjectGalleryPost[]>({
      operation: 'readItems',
      collection: 'posts',
      params: {
        filter: showAllProjects
          ? { _and: [{ type: { _eq: 'project' } }, { status: { _eq: 'published' } }] }
          : {
              _and: [
                { id: { _in: postIds } },
                { type: { _eq: 'project' } },
                { status: { _eq: 'published' } },
              ],
            },
        fields: [
          'id',
          'title',
          'slug',
          'client',
          'summary',
          'built_with',
          'cost',
          'status',
          'type',
          { image: ['id', 'title', 'description'] },
          { gallery: [{ directus_files_id: ['id', 'title', 'description'] }] },
        ],
        limit: -1,
      },
    });

    const cards: ProjectGalleryCard[] = showAllProjects
      ? (posts ?? [])
          .map((post) => buildCardFromPost(post))
          .filter((card): card is ProjectGalleryCard => card !== null)
      : buildProjectCards({
          junctionRows: junctionRows ?? [],
          posts: posts ?? [],
          blockFileIds,
        });

    return {
      cards,
      mode: cards.length > 0 ? 'project' : 'file',
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { cards: [], mode: 'file', error: message };
  }
}
